import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { Users } from 'lucide-react'
import type { DiningTable } from '@/types'
import { pktTime, type ReservationHold } from '@/lib/reservations'
import { Button } from '@/components/ui/button'
import { TableSeatRing } from '@/components/tables/TableSeatRing'
import { chairPositions, CHAIR_PAD } from '@/lib/tableChairs'
import {
  SEATED_EMPTY_STALE_MINUTES,
  TURNOVER_OVER_MINUTES,
  formatDuration,
  isSeatedEmpty,
  minutesSince,
  turnoverStatus,
  type TurnoverStatus,
} from '@/lib/tableTurnover'
import {
  DEFAULT_CANVAS_H,
  DEFAULT_CANVAS_W,
  type FloorDims,
  tablesBounds,
  fallbackMapDimensions,
  normalizeShape,
} from '@/lib/tableMapSizing'
import { anchorPanForZoom, clampPan, surfaceForZoom } from '@/lib/floorMapView'

/** Escalating style for the on-tile seated-time pill (green→amber→red). */
const TIMER_PILL_STYLES: Record<TurnoverStatus, string> = {
  fresh: 'bg-emerald-900/40 text-white',
  warn: 'bg-amber-500 text-white',
  over: 'bg-red-600 text-white',
}

const MIN_ZOOM = 0.2
// Manual zoom ceiling only — auto-fit has its own, much lower cap below.
const MAX_ZOOM = 4
// Auto-fit may upscale a sparse floor slightly past design size so it doesn't
// render postage-stamp small, but never beyond this. Without the cap, a floor
// with a single table fit-zoomed to 400% and the tile swallowed the viewport.
const FIT_MAX_UPSCALE = 1.25
const FIT_PADDING = 0.94
// A pointer that moves less than this stays a tap (table select); beyond it
// the gesture becomes a pan and the trailing click is swallowed.
const DRAG_THRESHOLD_PX = 8
// Snap the auto-fit scale to this step and only move zoom when it changes by
// more than HYSTERESIS — together these stop the sub-pixel refit oscillation
// (the "shaking") that a raw fit scale produced.
const FIT_STEP = 0.05
const FIT_HYSTERESIS = 0.03

type Props = {
  tables: DiningTable[]
  selectedTableId?: string
  onSelect?: (table: DiningTable) => void
  canSelect?: (table: DiningTable) => boolean
  className?: string
  viewportHeight?: number
  /**
   * Fill the parent's height instead of a fixed viewportHeight. When true the
   * component becomes a flex column and the scrollable canvas takes all
   * remaining space — used by the full-page server floor so the map is the hero
   * region rather than a boxed pane. `viewportHeight` is ignored.
   */
  fill?: boolean
  showControls?: boolean
  /**
   * Per-floor canvas size, already resolved via resolveFloorDims by the
   * caller. Drives the drawing surface so this read-only map matches the
   * admin layout builder's floor exactly. Falls back to the default canvas
   * when omitted (e.g. callers that don't know the floor).
   */
  floorLayout?: FloorDims
  /**
   * Epoch-ms clock supplied by the caller (a shared 1s ticker). When present,
   * occupied tiles render the live order details — order #, guest count, a
   * running seated timer, and the running total — like the competitor floor.
   * Omit it (e.g. the admin layout builder) and tiles fall back to the plain
   * number + seat-count label.
   */
  now?: number
  /**
   * Tables held for an upcoming booking (F-RSV-N1), keyed by table id. A
   * free tile that is held shows "Reserved 8:30 · Ahmed (4)" in amber.
   */
  holds?: Map<string, ReservationHold>
}

/** Small status pip in a corner of an occupied tile. */
function occupiedTint(table: DiningTable): string {
  const status = String((table as any).current_order?.status ?? '').toLowerCase()
  if (status.includes('ready')) return 'bg-emerald-300'
  if (status.includes('pending') || status.includes('placed') || status.includes('new')) return 'bg-amber-300'
  return 'bg-white/80'
}

export function TableFloorMap({
  tables,
  selectedTableId,
  onSelect,
  canSelect,
  className,
  viewportHeight = 300,
  fill = false,
  showControls = true,
  floorLayout,
  now,
  holds,
}: Props) {
  // Only tables the operator actually placed on the floor are drawn. Tables
  // without coordinates are the caller's problem to surface (as chips); drawing
  // them here would stack white cards in the corner via a positional fallback.
  const placed = useMemo(
    () =>
      tables.filter(
        (t) => Number.isFinite(Number(t.map_x)) && Number.isFinite(Number(t.map_y)),
      ),
    [tables],
  )

  // The floor's own drawing surface (matches the admin layout builder).
  const canvas = useMemo<FloorDims>(
    () => floorLayout ?? { width: DEFAULT_CANVAS_W, height: DEFAULT_CANVAS_H },
    [floorLayout],
  )

  // Fit to the *tables'* footprint (+ chairs), not the empty canvas — a few
  // tables should fill the viewport rather than be shrunk to a 1200×800 void.
  const bounds = useMemo(() => tablesBounds(placed, canvas, CHAIR_PAD), [placed, canvas])

  // Measure the scroll viewport so we can scale the floor to fit it. The
  // observer is coalesced through requestAnimationFrame so a burst of size
  // reports collapses into a single state update (belt-and-suspenders against
  // measurement churn on mount).
  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    let raf = 0
    const update = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => setViewport({ w: el.clientWidth, h: el.clientHeight }))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  const fitScale = useMemo(() => {
    if (!viewport.w || !viewport.h || bounds.width <= 0 || bounds.height <= 0) return 1
    const raw = Math.min(viewport.w / bounds.width, viewport.h / bounds.height) * FIT_PADDING
    // Snap to a step so sub-pixel viewport wobble can't nudge the scale.
    const snapped = Math.round(raw / FIT_STEP) * FIT_STEP
    return Math.max(MIN_ZOOM, Math.min(FIT_MAX_UPSCALE, snapped))
  }, [viewport, bounds])

  // Auto-fit by default; once the user zooms manually we leave their zoom alone
  // until they press "Fit". Re-fitting on resize / floor change keeps the whole
  // floor framed with no dead margin and nothing clipped. Hysteresis: only
  // adopt a new fit scale when it differs meaningfully — this is what finally
  // kills the shake (a tiny fit delta no longer re-lays-out the floor).
  const [zoom, setZoom] = useState(1)
  const [userZoomed, setUserZoomed] = useState(false)
  // Raw pan offset (screen px of the surface's top-left corner). Stored
  // unclamped; the render clamps it so the tables can never be dragged — or
  // zoomed — out of reach.
  const [pan, setPan] = useState({ x: 0, y: 0 })
  useEffect(() => {
    if (userZoomed) return
    setZoom((z) => (Math.abs(z - fitScale) > FIT_HYSTERESIS ? fitScale : z))
  }, [fitScale, userZoomed])

  // When the fit scale is upscale-capped (sparse floor — e.g. a single table),
  // the tables' footprint alone would render as a small cropped card floating
  // on the page background. surfaceForZoom expands the drawn floor so it fills
  // the viewport at the *current* zoom, with the tables centered on it — the
  // floor reads as a floor and the table stays at a believable size. Sizing to
  // the live zoom (not the fit scale) is what keeps the visible grid and the
  // tables' position in agreement after a manual zoom.
  const surfaceFor = useCallback(
    (z: number) => surfaceForZoom(bounds, viewport, z),
    [bounds, viewport],
  )
  const surface = useMemo(() => surfaceFor(zoom), [surfaceFor, zoom])
  const view = clampPan(pan, viewport, surface, zoom)

  // Latest-value refs so fast gesture streams (pinch/pan pointermoves arriving
  // between renders) always compute from the value they just wrote, not a
  // stale closure.
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom
  const panRef = useRef(pan)
  panRef.current = pan

  const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z))

  /** Zoom to `next`, keeping the floor point under viewport point (cx, cy) fixed. */
  const applyZoom = (next: number, cx: number, cy: number) => {
    const z = zoomRef.current
    const nz = clampZoom(next)
    const cur = surfaceFor(z)
    const p = clampPan(panRef.current, viewport, cur, z)
    const np = anchorPanForZoom(p, z, nz, { x: cx, y: cy }, cur, surfaceFor(nz))
    zoomRef.current = nz
    panRef.current = np
    setZoom(nz)
    setPan(np)
  }

  const nudge = (delta: number) => {
    setUserZoomed(true)
    applyZoom(Math.round((zoomRef.current + delta) * 100) / 100, viewport.w / 2, viewport.h / 2)
  }
  const fit = () => {
    setUserZoomed(false)
    zoomRef.current = fitScale
    panRef.current = { x: 0, y: 0 }
    setZoom(fitScale)
    setPan({ x: 0, y: 0 })
  }

  // --- Pointer-driven pan + pinch zoom -------------------------------------
  // The viewport never scrolls natively (overflow hidden + touch-action none),
  // so there is no scrollbar↔ResizeObserver feedback loop and no reliance on
  // `safe center` flex support in older Android WebViews. One finger (or a
  // mouse drag) pans; two fingers pinch-zoom around the pinch midpoint; a
  // sub-threshold tap still reaches the table buttons untouched.
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef({
    mode: 'idle' as 'idle' | 'press' | 'pan' | 'pinch',
    startX: 0,
    startY: 0,
    panAtStart: { x: 0, y: 0 },
    pinchDist: 0,
    pinchZoom: 1,
  })
  const justDragged = useRef(false)

  const localPoint = (e: ReactPointerEvent) => {
    const rect = viewportRef.current?.getBoundingClientRect()
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }
  }

  const beginPanFrom = (pt: { x: number; y: number }) => {
    const g = gesture.current
    g.startX = pt.x
    g.startY = pt.y
    g.panAtStart = clampPan(panRef.current, viewport, surfaceFor(zoomRef.current), zoomRef.current)
  }

  const onPointerDown = (e: ReactPointerEvent) => {
    const pt = localPoint(e)
    pointers.current.set(e.pointerId, pt)
    justDragged.current = false
    const g = gesture.current
    if (pointers.current.size === 1) {
      g.mode = 'press'
      beginPanFrom(pt)
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      g.mode = 'pinch'
      g.pinchDist = Math.hypot(a.x - b.x, a.y - b.y)
      g.pinchZoom = zoomRef.current
      justDragged.current = true
      setUserZoomed(true)
      // Capture both pointers so the pinch survives fingers sliding off-element.
      for (const id of pointers.current.keys()) viewportRef.current?.setPointerCapture(id)
    }
  }

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    const pt = localPoint(e)
    pointers.current.set(e.pointerId, pt)
    const g = gesture.current
    if (g.mode === 'press' && Math.hypot(pt.x - g.startX, pt.y - g.startY) > DRAG_THRESHOLD_PX) {
      g.mode = 'pan'
      justDragged.current = true
      viewportRef.current?.setPointerCapture(e.pointerId)
    }
    if (g.mode === 'pan') {
      const np = { x: g.panAtStart.x + (pt.x - g.startX), y: g.panAtStart.y + (pt.y - g.startY) }
      panRef.current = np
      setPan(np)
    } else if (g.mode === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      if (g.pinchDist > 0 && dist > 0) {
        applyZoom((g.pinchZoom * dist) / g.pinchDist, (a.x + b.x) / 2, (a.y + b.y) / 2)
      }
    }
  }

  const endPointer = (e: ReactPointerEvent) => {
    if (!pointers.current.delete(e.pointerId)) return
    const g = gesture.current
    if (pointers.current.size === 1) {
      // Pinch ended with one finger still down — continue as a plain pan.
      const [pt] = [...pointers.current.values()]
      g.mode = 'pan'
      beginPanFrom(pt)
    } else if (pointers.current.size === 0) {
      g.mode = 'idle'
    }
  }

  const onClickCapture = (e: ReactMouseEvent) => {
    if (justDragged.current) {
      e.preventDefault()
      e.stopPropagation()
      justDragged.current = false
    }
  }

  if (placed.length === 0) return null

  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-xl border border-stone-300/70 bg-[#efe7d8] shadow-inner dark:border-stone-700/70 dark:bg-[#241f1a] ${
        fill ? 'h-full min-h-0' : ''
      } ${className ?? ''}`}
    >
      {showControls && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full border border-stone-300/70 bg-card/90 p-1 dark:border-stone-700/70 shadow-sm backdrop-blur">
          <Button
            variant="ghost"
            size="sm"
            className="pointer-events-auto h-7 w-7 rounded-full p-0 text-base leading-none"
            onClick={() => nudge(-0.1)}
            aria-label="Zoom out"
          >
            −
          </Button>
          <span className="min-w-11 text-center text-xs font-medium tabular-nums text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="pointer-events-auto h-7 w-7 rounded-full p-0 text-base leading-none"
            onClick={() => nudge(0.1)}
            aria-label="Zoom in"
          >
            +
          </Button>
          <div className="mx-0.5 h-4 w-px bg-border" />
          <Button
            variant="ghost"
            size="sm"
            className="pointer-events-auto h-7 rounded-full px-3 text-xs font-medium"
            onClick={fit}
          >
            Fit
          </Button>
        </div>
      )}

      <div
        ref={viewportRef}
        className="relative flex-1 select-none overflow-hidden"
        style={{
          // Native scrolling stays off at every zoom: panning is pointer-driven,
          // so there is never a scrollbar (no scrollbar↔ResizeObserver shaking)
          // and the WebView never fights the gesture.
          touchAction: 'none',
          cursor: 'grab',
          ...(fill ? {} : { minHeight: 230, height: viewportHeight }),
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClickCapture={onClickCapture}
      >
        <div
          className="absolute left-0 top-0 rounded-lg"
          style={{
            width: surface.width,
            height: surface.height,
            transform: `translate(${view.x}px, ${view.y}px) scale(${zoom})`,
            transformOrigin: 'top left',
            backgroundColor: '#f7f1e8',
            backgroundImage:
              'linear-gradient(to right, rgba(74,53,33,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(74,53,33,0.05) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            boxShadow: 'inset 0 0 0 1px rgba(74,53,33,0.08)',
          }}
        >
            {placed.map((table) => {
              const occupied = table.has_active_order ?? table.is_occupied
              const selectable = canSelect ? canSelect(table) : true
              const selected = selectedTableId === table.id
              const hasW = table.map_w != null && Number.isFinite(Number(table.map_w)) && Number(table.map_w) > 0
              const hasH = table.map_h != null && Number.isFinite(Number(table.map_h)) && Number(table.map_h) > 0
              const fb = fallbackMapDimensions(table)
              const mapW = hasW ? Number(table.map_w) : fb.map_w
              const mapH = hasH ? Number(table.map_h) : fb.map_h
              const shape = normalizeShape(table.shape)
              const chairs = chairPositions(table.seating_capacity ?? 0, shape, mapW, mapH)
              // Live occupancy details — only when the caller supplies a clock
              // and the table carries its current order (server floor view).
              const order = table.current_order
              // Seated but nothing ordered yet (open tab, zero items) renders
              // in its own sky tint so a party-just-sat-down / abandoned tab
              // never masquerades as a table with a real order (F-CTRL-N8).
              const seatedEmpty = occupied && isSeatedEmpty(order)
              const chairColor = selected
                ? 'text-primary'
                : occupied
                  ? seatedEmpty
                    ? 'text-sky-400'
                    : 'text-emerald-500'
                  : 'text-muted-foreground/70'
              const showLive = now != null && occupied && !!order
              const seatAnchor = order?.table_opened_at ?? order?.created_at
              const seatedMins = showLive && seatAnchor ? minutesSince(seatAnchor, now!) : 0
              // Empty tabs escalate earlier: 30 min with no order is already a
              // "check on this table" signal, well before the camper thresholds.
              const seatedStatus: TurnoverStatus = seatedEmpty
                ? seatedMins >= TURNOVER_OVER_MINUTES
                  ? 'over'
                  : seatedMins >= SEATED_EMPTY_STALE_MINUTES
                    ? 'warn'
                    : 'fresh'
                : turnoverStatus(seatedMins)
              // A tile is "roomy" enough for the full stat stack when it isn't tiny.
              const roomy = mapH >= 70 && mapW >= 70
              const hold = !occupied ? holds?.get(table.id) : undefined
              return (
                <div
                  key={table.id}
                  className={`absolute ${chairColor}`}
                  style={{
                    left: `${Number(table.map_x) - surface.minX}px`,
                    top: `${Number(table.map_y) - surface.minY}px`,
                    width: `${mapW}px`,
                    height: `${mapH}px`,
                    transform: `rotate(${table.map_rotation ?? 0}deg)`,
                    transformOrigin: 'center center',
                  }}
                >
                  <TableSeatRing positions={chairs} tableWidth={mapW} tableHeight={mapH} />
                  <button
                    type="button"
                    onClick={() => selectable && onSelect?.(table)}
                    disabled={!selectable}
                    className={`relative flex h-full w-full flex-col items-center justify-center border text-sm shadow-sm transition-colors ${
                      selected ? 'ring-2 ring-primary ring-offset-1 ring-offset-[#f7f1e8]' : ''
                    } ${
                      occupied
                        ? seatedEmpty
                          ? 'border-sky-600 bg-sky-500 text-white shadow-sky-900/20 hover:bg-sky-500/90'
                          : 'border-emerald-600 bg-emerald-500 text-white shadow-emerald-900/20 hover:bg-emerald-500/90'
                        : hold
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-100 text-amber-950 hover:-translate-y-0.5 hover:border-amber-600 hover:shadow-md'
                          : 'border-input bg-white/95 text-foreground hover:-translate-y-0.5 hover:border-primary hover:shadow-md'
                    } ${selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                    style={{ borderRadius: shape === 'round' ? 999 : 12 }}
                  >
                    {occupied && (
                      <span
                        className={`absolute right-1.5 top-1.5 h-2 w-2 rounded-full ring-2 ${
                          seatedEmpty ? 'ring-sky-600/40 bg-white/80' : `ring-emerald-600/40 ${occupiedTint(table)}`
                        }`}
                      />
                    )}
                    <span className="font-bold leading-none tracking-tight">{table.table_number}</span>
                    {showLive ? (
                      <>
                        {/* Running seated timer — the headline live stat. */}
                        <span
                          className={`mt-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none ${
                            seatedEmpty && seatedStatus === 'fresh'
                              ? 'bg-sky-900/40 text-white'
                              : TIMER_PILL_STYLES[seatedStatus]
                          }`}
                        >
                          {formatDuration(seatedMins)}
                        </span>
                        {seatedEmpty
                          ? roomy && (
                              <span className="mt-1 text-[10px] leading-none text-white/90">
                                Seated · no order
                              </span>
                            )
                          : roomy &&
                            order!.guest_count > 0 && (
                              <span className="mt-1 flex items-center gap-0.5 text-[10px] leading-none text-white/90">
                                <Users className="h-2.5 w-2.5" />
                                {order!.guest_count}
                              </span>
                            )}
                      </>
                    ) : hold ? (
                      <>
                        <span className="mt-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                          Reserved {pktTime(hold.starts_at).replace(/\s?(AM|PM)$/i, '')}
                        </span>
                        {roomy && (
                          <span className="mt-1 max-w-full truncate px-1 text-[10px] leading-none text-amber-900 dark:text-amber-900">
                            {hold.guest_name.trim().split(/\s+/)[0]} ({hold.party_size})
                          </span>
                        )}
                      </>
                    ) : (
                      <span className={`mt-0.5 text-[11px] ${occupied ? 'text-white/85' : 'text-muted-foreground'}`}>
                        {table.seating_capacity} seats
                      </span>
                    )}
                  </button>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
